import { useEffect } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";

import { checkTabIsActive, IdValue, Spinner } from "@prequel/shared-components";

import type { DestinationResource } from "@/api/import/destinations";
import { useDestination } from "@/api/import/destinations";
import DetailPageLayout from "@/components/import/DetailPageLayout";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type DestinationContext = {
  destination: DestinationResource;
};

export function useDestinationContext(): DestinationContext {
  return useOutletContext<DestinationContext>();
}

const DestinationPage = () => {
  const destinationID = useRequiredParam("destinationID");
  const { addToast } = useToast();
  const { data: destination, error, isLoading } = useDestination(destinationID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load destination." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!destination) {
    return <Navigate to="/import/destinations" replace />;
  }

  return <Outlet context={{ destination }} />;
};

export const DestinationDetailsPage = () => {
  const navigate = useNavigate();
  const { destination } = useDestinationContext();
  const basePath = `/import/destinations/${destination.id}`;

  const tabs = [
    <NavLink key="Overview" to={basePath} className={checkTabIsActive} end>
      Overview
    </NavLink>,
    <NavLink key="Streams" to={`${basePath}/streams`} className={checkTabIsActive} end>
      Streams
    </NavLink>,
    <NavLink key="Admin" to={`${basePath}/admin`} className={checkTabIsActive} end>
      Admin
    </NavLink>,
  ];

  return (
    <DetailPageLayout
      title={destination.name}
      onGoBack={() => navigate("/import/destinations")}
      subheading={
        <div className="flex items-center gap-2">
          <IdValue id={destination.id} />
        </div>
      }
      tabs={tabs}
    >
      <Outlet context={{ destination }} />
    </DetailPageLayout>
  );
};

export default DestinationPage;
